import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
const toast = { success: (m) => console.log("[toast]", m), error: (m) => console.error("[toast]", m), info: (m) => console.log("[toast]", m) };
import PageContainer, { PageHeader } from "@/components/PageContainer";
import api from "@/lib/assessments";
import { readAll } from "@/lib/assessments";
const formatApiError = (e) => (e && e.message) || "Unknown error";
import { Save, ChevronLeft, FileText } from "lucide-react";
import { fleschKincaidGrade, interpretReadingLevel } from "@/data/instruments";

const SAMPLE = `Recovery is not a straight line. Some days you will feel strong. Other days you will not. Both are part of the process.\n\nYour treatment team is here to help you plan for both. When you feel strong, we build. When you struggle, we help you stay safe.`;

export default function ReadingLevel() {
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [material, setMaterial] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const result = fleschKincaidGrade(text);
  const interp = interpretReadingLevel(result?.grade);

  const save = async () => {
    if (!result) {
      toast.error("Paste text to analyze.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/assessments", {
        kind: "reading_level",
        patient_identifier: material || null,
        responses: { text, ...result },
        score: result.grade,
        interpretation: interp.band,
        notes: notes || null,
      });
      toast.success("Reading level analysis saved.");
      navigate("/portal/continuum/tools/history");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageContainer testid="reading-page" wide>
      <Link to="/portal/continuum/tools" className="text-sm text-ink-muted hover:text-navy inline-flex items-center gap-1 mb-4" data-testid="reading-back">
        <ChevronLeft size={14} strokeWidth={2} /> All tools
      </Link>

      <PageHeader overline="Portal · Tool" title="Reading Level Analyzer"
        subtitle="Paste treatment materials, patient handouts, or discharge summaries. Get Flesch-Kincaid grade level and Flesch Reading Ease. Target: grade 6 – 8." />

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <div className="card p-5">
            <label className="label">Material name (optional)</label>
            <input className="field" value={material} onChange={(e) => setMaterial(e.target.value)} placeholder="e.g. Intake handbook, section 3" data-testid="reading-material" />
          </div>

          <div className="card p-5">
            <div className="flex items-baseline justify-between mb-2">
              <label className="label">Text to analyze</label>
              <button
                type="button"
                onClick={() => setText(SAMPLE)}
                className="text-xs text-teal hover:underline"
                data-testid="reading-load-sample"
              >
                Load sample text
              </button>
            </div>
            <textarea
              className="field min-h-[280px] font-mono text-sm"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Paste at least a few sentences here."
              data-testid="reading-input"
            />
            <div className="text-xs text-ink-muted mt-2">
              {result ? `${result.words} words · ${result.sentences} sentence${result.sentences === 1 ? "" : "s"}` : "0 words"}
            </div>
          </div>

          <div className="card p-5">
            <label className="label">Notes</label>
            <textarea className="field min-h-[80px]" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="reading-notes" />
          </div>
        </div>

        <aside className="lg:sticky lg:top-6 self-start">
          <div className="card p-6" data-testid="reading-summary">
            <FileText size={20} strokeWidth={1.5} className="text-teal mb-2" />
            <div className="overline mb-2">Grade level</div>
            {result ? (
              <>
                <div className="text-5xl font-semibold text-navy">{result.grade}</div>
                <div className="mt-3">
                  <div className={`badge mb-2 ${result.grade <= 8 ? "badge-success" : result.grade <= 12 ? "badge-teal" : "badge-rust"}`}>
                    {interp.band}
                  </div>
                  <p className="text-sm text-ink leading-relaxed">{interp.detail}</p>
                </div>
                <div className="rule my-4" />
                <ul className="text-sm space-y-1">
                  <li className="flex items-center justify-between text-ink">
                    <span>Reading ease</span><span className="text-navy font-medium">{result.ease}</span>
                  </li>
                  <li className="flex items-center justify-between text-ink">
                    <span>Words</span><span className="text-navy font-medium">{result.words}</span>
                  </li>
                  <li className="flex items-center justify-between text-ink">
                    <span>Sentences</span><span className="text-navy font-medium">{result.sentences}</span>
                  </li>
                </ul>
              </>
            ) : (
              <div className="text-sm text-ink-muted">Paste text to analyze.</div>
            )}

            <div className="rule my-4" />
            <button onClick={save} disabled={saving || !result} className="btn btn-primary w-full justify-center" data-testid="reading-save">
              {saving ? "Saving..." : (<><Save size={14} strokeWidth={2} /> Save to history</>)}
            </button>
          </div>
        </aside>
      </div>
    </PageContainer>
  );
}
